import { useState, useCallback, useRef, useEffect } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import type { DocumentRecord } from '@/types/catalog'
import { buildOrderedRows } from './catalogRowOrder'
import type { SortState, SetGroup } from './catalogRowOrder'
import type { RowAction } from '@/components/DataTable/DataTableTypes'
import StatusTag from '@/components/StatusTag'
import { getDocumentStatus } from '@/utils/documentStatus'
import { timeAgo } from '@/utils/timeUtils'
import RowActionsDropdown from '@/components/DataTable/RowActionsDropdown'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { ChevronUp, ChevronDown, ChevronRight, Loader2, FileText, SearchX, Upload, Unlink2 } from 'lucide-react'
import { useTranslation } from '@/i18n'

// Row-ordering logic lives in a plain module (keeps this file component-only for
// react-refresh, and lets the comparator be unit-tested directly). SetGroup /
// SortState are re-exported here for existing importers.
export type { SetGroup, SortState, MergedRow } from './catalogRowOrder'

interface CatalogTableV2Props {
  data: DocumentRecord[]
  loading: boolean
  onRowClick: (doc: DocumentRecord) => void
  rowActions: RowAction[]
  sort: SortState
  onSortChange: (sort: SortState) => void
  onRename?: (id: string, newName: string) => void
  getDocTypeLabel: (type: string) => string
  selectedRows: Set<string>
  onRowSelect: (id: string, checked: boolean) => void
  onSelectAll: (checked: boolean) => void
  onUploadClick?: () => void
  /** FS-567: true when search or any filter narrows the result set, so a
   *  zero-row table means "no matches" rather than "no data onboarded yet". */
  hasActiveFilters?: boolean
  /** FS-567: clears search + all filters from the no-matches empty state. */
  onClearFilters?: () => void
  setGroups?: SetGroup[]
  onSetClick?: (group: SetGroup) => void
  onUngroupSet?: (setId: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  loadingMore?: boolean
}

interface Column {
  key: string
  label: string
  width: string
  flex?: boolean
  sortable?: boolean
  sortKey?: string
}

// `label` holds an i18n key resolved with t() at render time.
const COLUMNS: Column[] = [
  { key: 'name', label: 'common:table_columns.name', width: '', flex: true, sortable: true },
  { key: 'type', label: 'common:table_columns.type', width: 'w-[144px]', sortable: true },
  { key: 'status', label: 'common:table_columns.status', width: 'w-[144px]', sortable: true },
  { key: 'uploader_name', label: 'data_catalog:uploaded_by', width: 'w-[144px]', sortable: true, sortKey: 'uploaded_by' },
  { key: 'created_at', label: 'data_catalog:table.column_creation_date', width: 'w-[180px]', sortable: true, sortKey: 'upload_started_at' },
  { key: 'actions', label: '', width: 'w-[36px]', sortable: false },
]

function SortIcon({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  if (!active) return null
  return direction === 'asc'
    ? <ChevronUp className="size-3.5 text-foreground" />
    : <ChevronDown className="size-3.5 text-foreground" />
}

function SetRow({
  group, isExpanded, onToggle, selectedRows, onRowSelect, onRowClick, rowActions, onRename, getDocTypeLabel, onSetClick, onUngroupSet,
}: {
  group: SetGroup
  isExpanded: boolean
  onToggle: () => void
  selectedRows: Set<string>
  onRowSelect: (id: string, checked: boolean) => void
  onRowClick: (doc: DocumentRecord) => void
  rowActions: RowAction[]
  onRename?: (id: string, newName: string) => void
  getDocTypeLabel: (type: string) => string
  onSetClick?: (group: SetGroup) => void
  onUngroupSet?: (setId: string) => void
}) {
  const { t } = useTranslation()
  const firstDoc = group.documents[0]
  if (!firstDoc) return null

  return (
    <>
      {/* Set parent row */}
      <div
        className="flex items-center h-[52px] border-b border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50 transition-colors cursor-pointer"
        onClick={() => onSetClick ? onSetClick(group) : onToggle()}
      >
        <span className="w-[40px] shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
<Checkbox
            checked={
              group.documents.every((d) => selectedRows.has(d.id))
                ? true
                : group.documents.some((d) => selectedRows.has(d.id))
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(checked) =>
              group.documents.forEach((d) => onRowSelect(d.id, checked === true))
            }
            className="cursor-pointer"
          />
        </span>
        <span className="flex-1 min-w-0 px-2 flex items-center gap-2">
          <span onClick={(e) => { e.stopPropagation(); onToggle() }} className="cursor-pointer">
            <ChevronRight className={`size-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </span>
          <span className="text-sm font-medium truncate">{group.setName}</span>
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            {group.documents.length} {t('data_catalog:table.set_items_badge')}
          </span>
        </span>
        <span className="w-[144px] shrink-0 px-2 text-sm text-muted-foreground">
          {getDocTypeLabel(firstDoc.type)}
        </span>
        <span className="w-[144px] shrink-0 px-2">
          <StatusTag status={getDocumentStatus(firstDoc)} />
        </span>
        <span className="w-[144px] shrink-0 px-2 text-sm text-muted-foreground truncate">
          {firstDoc.uploader_name || ''}
        </span>
        <span className="w-[180px] shrink-0 px-2 text-sm text-muted-foreground whitespace-nowrap">
          {timeAgo(firstDoc.upload_started_at || firstDoc.created_at)}
        </span>
        <span className="w-[36px] shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {onUngroupSet && (
            <button
              type="button"
              title={t('data_catalog:table.ungroup_set_tooltip')}
              onClick={() => onUngroupSet(group.setId)}
              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <Unlink2 className="size-4" />
            </button>
          )}
        </span>
      </div>
      {/* Expanded child rows */}
      {isExpanded && group.documents.map((doc) => {
        const isSelected = selectedRows.has(doc.id)
        return (
          <div
            key={doc.id}
            className={`flex items-center h-[52px] border-b border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50 transition-colors cursor-pointer ${isSelected ? 'bg-muted/30' : ''}`}
            onClick={() => onRowClick(doc)}
          >
            <span className="w-[40px] shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onRowSelect(doc.id, checked === true)}
                className="cursor-pointer"
              />
            </span>
            <span className="flex-1 min-w-0 px-2 flex items-center gap-2 ps-10">
              <span className="text-sm truncate">{doc.name}</span>
            </span>
            <span className="w-[144px] shrink-0 px-2 text-sm text-muted-foreground">
              {getDocTypeLabel(doc.type)}
            </span>
            <span className="w-[144px] shrink-0 px-2">
              <StatusTag status={getDocumentStatus(doc)} />
            </span>
            <span className="w-[144px] shrink-0 px-2 text-sm text-muted-foreground truncate">
              {doc.uploader_name || ''}
            </span>
            <span className="w-[180px] shrink-0 px-2 text-sm text-muted-foreground whitespace-nowrap">
              {timeAgo(doc.upload_started_at || doc.created_at)}
            </span>
            <span className="w-[36px] shrink-0" onClick={(e) => e.stopPropagation()}>
              <RowActionsDropdown
                actions={rowActions}
                id={doc.id}
                rowType={doc.type}
                document={doc}
                onRename={onRename ? (newName: string) => onRename(doc.id, newName) : undefined}
              />
            </span>
          </div>
        )
      })}
    </>
  )
}

export default function CatalogTableV2({
  data, loading, onRowClick, rowActions, sort, onSortChange, onRename, getDocTypeLabel,
  selectedRows, onRowSelect, onSelectAll, onUploadClick, hasActiveFilters, onClearFilters,
  setGroups = [], onSetClick, onUngroupSet,
  onLoadMore, hasMore, loadingMore,
}: CatalogTableV2Props) {
  const { t } = useTranslation()
  const [expandedSets, setExpandedSets] = useState<Set<string>>(new Set())
  const sentinelRef = useRef<HTMLDivElement>(null)

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!onLoadMore || !hasMore) return
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore()
      },
      { rootMargin: '200px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [onLoadMore, hasMore])

  const toggleSet = useCallback((setId: string) => {
    setExpandedSets((prev) => {
      const next = new Set(prev)
      if (next.has(setId)) next.delete(setId)
      else next.add(setId)
      return next
    })
  }, [])

  const handleSort = (col: Column) => {
    if (!col.sortable) return
    const sortKey = col.sortKey || col.key
    if (sort.key === sortKey) {
      onSortChange({ key: sortKey, direction: sort.direction === 'asc' ? 'desc' : 'asc' })
    } else {
      onSortChange({ key: sortKey, direction: 'asc' })
    }
  }

  // IDs belonging to any set group (to exclude from flat list)
  const setDocIds = new Set(setGroups.flatMap((g) => g.documents.map((d) => d.id)))
  const flatDocs = data.filter((d) => !setDocIds.has(d.id))

  const selectableRows = data.filter((d) => d.status !== 'uploading')
  const allSelected = selectableRows.length > 0 && selectableRows.every((d) => selectedRows.has(d.id))
  const someSelected = selectableRows.some((d) => selectedRows.has(d.id))

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      {/* min-width track keeps columns reachable via one horizontal scroll
          instead of collapsing the name column at narrow widths. */}
      <div className="min-w-[880px] px-6">
      {/* Header */}
      <div className="flex items-center h-[52px] border-b border-[#e5e5e5] dark:border-dark-border sticky top-0 bg-white dark:bg-dark-card z-10">
        {/* Checkbox */}
        <span className="w-[40px] shrink-0 flex items-center justify-center">
          <Checkbox
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            onCheckedChange={(checked) => onSelectAll(checked === true)}
            className="cursor-pointer"
          />
        </span>
        {COLUMNS.map((col) => {
          const isActive = sort.key === (col.sortKey || col.key)
          return (
            <span
              key={col.key}
              className={`${col.flex ? 'flex-1 min-w-0' : col.width} shrink-0 px-2 text-sm font-medium text-muted-foreground flex items-center gap-1 ${col.sortable ? 'cursor-pointer select-none hover:text-foreground' : ''}`}
              onClick={() => handleSort(col)}
            >
              {col.label ? t(col.label as never) : null}
              {col.sortable && <SortIcon active={isActive} direction={sort.direction} />}
            </span>
          )
        })}
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : data.length === 0 && setGroups.length === 0 ? (
        hasActiveFilters ? (
          /* FS-567: zero rows because search/filters matched nothing — the
             "No boards yet" onboarding state would be wrong here. */
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <SearchX className="size-10 text-muted-foreground/50" />
            <div className="text-sm font-medium text-foreground">{t('data_catalog:table.filtered_empty_heading')}</div>
            <div className="text-xs text-muted-foreground">{t('data_catalog:table.filtered_empty_description')}</div>
            {onClearFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="mt-2 inline-flex items-center px-4 h-9 text-sm font-medium rounded-md border border-input bg-background text-foreground hover:bg-muted transition-colors"
              >
                {t('data_catalog:filter.clear_all')}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <FileText className="size-10 text-muted-foreground/50" />
            <div className="text-sm font-medium text-foreground">{t('data_catalog:table.empty_state_heading')}</div>
            <div className="text-xs text-muted-foreground">{t('data_catalog:table.empty_state_description')}</div>
            {onUploadClick && (
              /* Same Upload affordance as the filter bar — files are
                 uploaded here, not boards. */
              <IntuiButton onClick={onUploadClick} className="mt-2 h-9">
                <Upload className="size-4" />
                {t('common:buttons.upload')}
              </IntuiButton>
            )}
          </div>
        )
      ) : (
        <>
          {/* Merge set groups + flat docs into one list, ordered by the active
              column (default: upload start time, newest first). See buildOrderedRows. */}
          {(() => {
            const items = buildOrderedRows(flatDocs, setGroups, sort)

            return items.map((item) => {
              if (item.kind === 'set') {
                return (
                  <SetRow
                    key={item.group.setId}
                    group={item.group}
                    isExpanded={expandedSets.has(item.group.setId)}
                    onToggle={() => toggleSet(item.group.setId)}
                    selectedRows={selectedRows}
                    onRowSelect={onRowSelect}
                    onRowClick={onRowClick}
                    rowActions={rowActions}
                    onRename={onRename}
                    getDocTypeLabel={getDocTypeLabel}
                    onSetClick={onSetClick}
                    onUngroupSet={onUngroupSet}
                  />
                )
              }

              const doc = item.doc
              const isSelected = selectedRows.has(doc.id)
              const isUploading = doc.status === 'uploading'
              return (
                <div
                  key={doc.id}
                  className={`flex items-center h-[52px] border-b border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50 transition-colors cursor-pointer ${isSelected ? 'bg-muted/30' : ''}`}
                  onClick={() => onRowClick(doc)}
                >
                  {/* Checkbox */}
                  <span className="w-[40px] shrink-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      disabled={isUploading}
                      onCheckedChange={(checked) => onRowSelect(doc.id, checked === true)}
                      className="cursor-pointer disabled:opacity-40"
                    />
                  </span>
                  {/* Name */}
                  <span className="flex-1 min-w-0 px-2 flex items-center gap-2">
                    <span className="text-sm truncate">{doc.name}</span>
                  </span>
                  {/* Type */}
                  <span className="w-[144px] shrink-0 px-2 text-sm text-muted-foreground">
                    {getDocTypeLabel(doc.type)}
                  </span>
                  {/* Status */}
                  <span className="w-[144px] shrink-0 px-2">
                    <StatusTag status={getDocumentStatus(doc)} />
                  </span>
                  {/* Uploader */}
                  <span className="w-[144px] shrink-0 px-2 text-sm text-muted-foreground truncate">
                    {doc.uploader_name || ''}
                  </span>
                  {/* Creation Date */}
                  <span className="w-[180px] shrink-0 px-2 text-sm text-muted-foreground whitespace-nowrap">
                    {timeAgo(doc.upload_started_at || doc.created_at)}
                  </span>
                  {/* Actions */}
                  <span className="w-[36px] shrink-0" onClick={(e) => e.stopPropagation()}>
                    <RowActionsDropdown
                      actions={rowActions}
                      id={doc.id}
                      rowType={doc.type}
                      document={doc}
                      onRename={onRename ? (newName: string) => onRename(doc.id, newName) : undefined}
                    />
                  </span>
                </div>
              )
            })
          })()}

          {/* Infinite scroll sentinel + loading spinner */}
          {loadingMore && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {hasMore && <div ref={sentinelRef} className="h-1" />}
        </>
      )}
      </div>
    </div>
  )
}
